import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import PageComponent from "components/Page/PageComponent";
import { QuickLookupStyle } from "./QuickLookupStyle";
import ActivityTypesField from "features/activity/ActivityTypesField";
import FilterService, { fieldTypes } from "services/filterService";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetQuickLookupPricingManagementQuery } from "features/pricing/pricingSlice";
import { orderBy } from "lodash";
import useSortOptions from "hooks/useSortOptions";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import FormatService from "services/formatService";
import { arrayToObject } from "utils/object-util";
import { Tooltip } from "@material-ui/core";
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import PersonIcon from '@material-ui/icons/Person';
import ViewModuleOutlinedIcon from '@material-ui/icons/ViewModuleOutlined';
import { ActivityItemPriceModes, PRICING_MODES } from "features/pricingManagement/CreateActivityAndPricing/ActivityItemPricing/PricingCalculator/PricingCalculatorConsts";
import { useState } from "react";
import Alert from '@material-ui/lab/Alert';


const QuickLookup = () => {
    const classes = QuickLookupStyle();
    const [sortOptions, sortData] = useSortOptions();
    const [filters, setFilters] = useState({});


    const onDropFilter = (value, prop) => {
        setFilters({ ...filters, [prop]: { value } });
    };

    const onActivityFilterChange = (rawId, { isBundle, jsonValue } = {}) => {
        if (isBundle) {
            setFilters(prev => ({ ...prev, bundleId: { value: rawId }, activityItemId: undefined, activityDropdownValue: jsonValue }));
        } else {
            setFilters(prev => ({ ...prev, activityItemId: { value: rawId }, bundleId: undefined, activityDropdownValue: jsonValue }));
        }
    };

    const pricingParams = {
        clientId: filters?.clientId?.value,
        lotId: filters?.lotId?.value,
        activityId: filters?.activityItemId?.value,
        bundleId: filters?.bundleId?.value
    };

    const allFiltersSet = !!(pricingParams.clientId && pricingParams.lotId && (pricingParams.activityId || pricingParams.bundleId));

    const getMissingFiltersMessage = () => {
        const missing = [];
        if (!pricingParams.clientId) missing.push('Client');
        if (!pricingParams.activityId && !pricingParams.bundleId) missing.push('Activity / Bundle');
        if (!pricingParams.lotId) missing.push('Lot');

        if (missing.length === 0) return '';
        return `Please select: ${missing.join(', ')}`;
    };

    let { data: clients, error: clientError, isLoading: isLoadingClients } = useGetClientsQuery(null);
    let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery();


    let { data: activityPricing, error: activityPricingError, isFetching: isLoadingActivityPricing } = useGetQuickLookupPricingManagementQuery(pricingParams, {
        skip: !allFiltersSet
    });
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();

    const lotLookup = arrayToObject(lots)
    const clientLookup = arrayToObject(clients)
    const vendorLookup = arrayToObject(vendors)

    activityPricing = activityPricing
        ?.map(activityPrice => {
            return {
                ...activityPrice,
                activityItem: activityPrice?.displayName,
                lot: lotLookup?.[activityPrice?.lotId]?.name,
                vendor: vendorLookup?.[activityPrice?.vendorId]?.name,
                formattedPrice: FormatService.formatCurrency(activityPrice?.price),
                clientName: clientLookup?.[activityPrice.clientId]?.name,
            }
        });


    const filterConfig = [
        { type: fieldTypes.TEXT, size: 2, title: 'Client', key: 'clientId', values: clients },
        { type: fieldTypes.COMPONENT, size: 2, component: <ActivityTypesField onChange={onActivityFilterChange} value={filters?.activityDropdownValue} required={false} includeBundles={true} /> },
        { type: fieldTypes.LOT, size: 2, title: 'Lot', key: 'lotId', values: lots },
    ];

    const isBundle = !!filters?.bundleId?.value;

    let activityPricingColumns = [
        {
            name: "Client",
            sortKey: "clientName",
            key: "clientName",
            component: (row) => <>
                <div>{clientLookup?.[row?.clientId]?.name}</div>
            </>
        },
        {
            name: "Activity",
            sortKey: "activityItem",
            key: "activityItem",
            component: (row) => (
                <div className={classes.priceRow}>
                    <div>{row?.displayName}</div>
                    {(!!isBundle || !!row?.bundleId) && (
                        <Tooltip title="Bundle" arrow>
                            <ViewModuleOutlinedIcon className={classes.priceIcon} />
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            name: "Lot",
            sortKey: "lot",
            key: "lot",
            component: (row) => <>
                <div>{lotLookup?.[row?.lotId ?? filters?.lotId?.value]?.name}</div>
            </>
        },
        ...(!isBundle ? [
            {
                name: "Vendor",
                sortKey: "vendor",
                key: "vendor",
                component: (row) => <>
                    <div>{vendorLookup?.[row?.vendorId]?.name ?? '-'}</div>
                </>
            },
            {
                name: "Vendor Cost",
                sortKey: "vendorCost",
                key: "vendorCost",
                component: (row) => <>
                    <div>{row?.price ? FormatService.formatCurrency(row?.price) : '-'}</div>
                </>
            },
        ] : []),
        {
            name: "Client Price",
            sortKey: "clientPrice",
            key: "clientPrice",

            component: (row) => (
                <div className={classes.priceRow}>
                    <div>{FormatService.formatCurrency(row?.charge)}</div>
                    {row?.priceMode === ActivityItemPriceModes.FLAT_FEE && (
                        <Tooltip title={PRICING_MODES.FLAT_FEE.name} arrow>
                            <PersonIcon className={classes.priceIcon} />
                        </Tooltip>
                    )}
                    {row?.priceMode === ActivityItemPriceModes.SURCHARGE && (
                        <Tooltip title={PRICING_MODES.SURCHARGE.name} arrow>
                            <MonetizationOnOutlinedIcon className={classes.priceIcon} />
                        </Tooltip>
                    )}
                </div>
            )
        },
        ...(!isBundle ? [
            {
                name: "Margin",
                sortKey: "margin",
                key: "margin",
                component: (row) => <>
                    <div>{row?.margin ? FormatService.formatPercent(row?.margin) : '-'}</div>
                </>
            },
        ] : []),
    ];


    return (
        <PageComponent className={classes.card} header={
            <DataListHeader
                data={activityPricing}
                dropDownsConfig={filterConfig}
                filters={filters}
                onDropFilter={onDropFilter}
            />}>
            {!allFiltersSet ? (
                <EmptyPlaceholder text={getMissingFiltersMessage()} />
            ) : isLoadingActivityPricing ? (
                <LoadingSpinner loading={true} />
            ) : (
                <div>
                    <Alert severity="warning" className={classes.alert}>
                        {"Note: the pricings that are displayed are based on the preferred vendor"}
                    </Alert>
                    <DataTable
                        columns={activityPricingColumns}
                        rows={orderBy(
                            activityPricing || [],
                            sortOptions.columnToSort,
                            sortOptions.sortDirection
                        )}
                        rowIdentifier='id'
                        onSort={sortData}
                        sortDirection={sortOptions.sortDirection}
                        columnToSort={sortOptions.columnToSort}
                        noItemsMessage='There are no activity prices matching this criteria'
                        headerStyle={{
                            paddingLeft: '1rem',
                        }}
                        data={activityPricing}
                    />
                </div>
            )}
        </PageComponent>
    )

};

export default QuickLookup;
